import React, { useEffect } from 'react';
import { ThemeProvider as NextThemeProvider, useTheme as useNextTheme } from 'next-themes';

/**
 * Theme plumbing.
 *
 * `next-themes` was already a dependency — `components/ui/sonner` reads its
 * `useTheme` — so it owns the mechanics here rather than a second, parallel
 * implementation: the `dark` class on `<html>`, the localStorage write, the
 * `prefers-color-scheme` listener behind the `system` option, and `color-scheme`
 * so the browser's own chrome (scrollbars, date pickers, autofill) matches.
 *
 * What it does *not* do in a Vite SPA is run before first paint — it has no
 * server to render its blocking script — so `index.html` carries a small inline
 * copy of the same logic. The two must agree on `STORAGE_KEY`.
 */

/** Shared with the pre-paint script in `index.html`. Changing one changes both. */
export const STORAGE_KEY = 'byz-theme';

export type ThemeChoice = 'light' | 'dark' | 'system';

/** The address bar / task switcher tint, per resolved theme. */
const THEME_COLOR: Record<'light' | 'dark', string> = {
  light: '#F2F2F2',
  dark: '#0B0B0C',
};

/**
 * Keep `<meta name="theme-color">` in step with the resolved theme.
 *
 * The tag is static in `index.html`, so without this the mobile browser chrome
 * keeps the light tint while the page underneath goes dark — the seam shows on
 * exactly the devices most likely to be in dark mode.
 */
const ThemeColorSync: React.FC = () => {
  const { resolvedTheme } = useNextTheme();

  useEffect(() => {
    const tint = THEME_COLOR[resolvedTheme === 'dark' ? 'dark' : 'light'];
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');

    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }

    meta.content = tint;
  }, [resolvedTheme]);

  return null;
};

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <NextThemeProvider
    attribute="class"
    storageKey={STORAGE_KEY}
    defaultTheme="system"
    enableSystem
    enableColorScheme
    // Without this every themed property animates at once through whatever
    // `transition-colors` each component declares, and the switch reads as a
    // slow smear rather than a flip.
    disableTransitionOnChange
  >
    <ThemeColorSync />
    {children}
  </NextThemeProvider>
);

/**
 * The app's view of the theme.
 *
 * `choice` is what the user picked and what the preference UI should show —
 * including `system`. `resolved` is what is actually on screen right now, and is
 * the one to branch rendering on (picking an image, say). It is `undefined` on
 * the very first render, before the provider has read storage; treat that as
 * "not known yet" rather than defaulting to light, or a dark-mode user sees a
 * flash of the wrong control state.
 */
export const useTheme = () => {
  const { theme, resolvedTheme, setTheme, systemTheme } = useNextTheme();

  return {
    choice: (theme ?? 'system') as ThemeChoice,
    resolved: resolvedTheme as 'light' | 'dark' | undefined,
    systemTheme: systemTheme as 'light' | 'dark' | undefined,
    setTheme: setTheme as (next: ThemeChoice) => void,
    isDark: resolvedTheme === 'dark',
  };
};

export default ThemeProvider;
